import { Component } from '@angular/core';
import { Color } from '@lsq-frontend/common-ui';
import { BackdropService, ClaimService, Icons } from '@lsq-frontend/common-ui';
import { Modal, ModalWorkflowService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { map, pairwise, shareReplay } from 'rxjs/operators';
import { BuyerAccountInvoiceUploadModalComponent } from './buyer-account-invoice-upload-modal.component';
import { BuyerAccountRemittanceInstructionsUploadModalComponent } from './buyer-account-remittance-upload-modal.component';
import { BuyerAccountRoutingInstructionsUploadModalComponent } from './buyer-account-routing-instruction-upload-modal.component';
import { BuyerAccountSupplierUploadModalComponent } from './buyer-account-supplier-upload-modal.component';
import { InvoiceUploadSuccessModal } from './buyer-account-upload-success-modal/invoice-upload-success-modal';
import { RemittanceInstructionsUploadSuccessModal } from './buyer-account-upload-success-modal/remittance-instructions-upload-success-modal';
import { SupplierUploadSuccessModal } from './buyer-account-upload-success-modal/supplier-upload-success-modal';
import { RoutingInstructionsUploadSuccessModal } from './buyer-account-upload-success-modal/routing-instructions-upload-success-modal';
import { merge } from 'rxjs';
import { SupplyChainDirectBuyerAccountSharedService } from '../../supply-chain-direct-buyer-account-shared.service';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';

@Component({
    selector: 'buyer-account-upload-button',
    template: `
        <div class="buyer-account-upload-button">
            <lsq-button
                (buttonClick)="toggleDropdown()"
                [disabled]="isAddInvoicesDisabled$ | async | notNull"
                size="md"
            >
                <span i18n>Upload Files</span>
            </lsq-button>
            <div class="upload-dropdown" *ngIf="showDropdown">
                <lsq-button
                    appearance="secondary"
                    size="block"
                    (buttonClick)="toggleInvoiceUploadModal()"
                >
                    <span i18n="button to add invoices">Upload Invoices</span>
                </lsq-button>
                <lsq-button
                    appearance="secondary"
                    size="block"
                    [style.marginTop]="'0.5rem'"
                    (buttonClick)="toggleSupplierUploadModal()"
                >
                    <span i18n="button to add suppliers">Upload Suppliers</span>
                </lsq-button>

                <lsq-button
                    appearance="secondary"
                    *ngIf="
                        (isABuyerUser$ | async) &&
                        !(buyerAccountSharedService.manageAllPayments$ | async)
                    "
                    size="block"
                    [style.marginTop]="'0.5rem'"
                    (buttonClick)="toggleRemittanceInstructionUploadModal()"
                >
                    <span i18n="button to add remittance instructions">
                        Upload Remittance Instructions
                    </span>
                </lsq-button>
                <lsq-button
                    appearance="secondary"
                    *ngIf="
                        (isABuyerUser$ | async) &&
                        (buyerAccountSharedService.manageAllPayments$ | async)
                    "
                    size="block"
                    [style.marginTop]="'0.5rem'"
                    (buttonClick)="toggleRoutingInstructionUploadModal()"
                >
                    <span i18n="button to add routing instructions">
                        Upload Routing Instructions
                    </span>
                </lsq-button>
            </div>
        </div>
    `,
    styleUrls: ['./buyer-account-upload-button.component.scss']
})
export class BuyerAccountUploadButtonComponent {
    public readonly icons = Icons;
    public readonly colors = Color;
    public showDropdown = false;

    public isABuyerUser$ = this._claimService.isOn(DashboardClaim.FinancingBuyerPortal);

    public isAddInvoicesDisabled$ = merge(
        this._claimService.isOn(DashboardClaim.FinancingAccountPortalEditInvoices),
        this._claimService.isOn(DashboardClaim.FinancingBuyerPortalEditInvoices)
    ).pipe(
        pairwise(),
        map(([hasAccountClaim, hasBuyerClaim]) => !hasAccountClaim && !hasBuyerClaim),
        shareReplay(1)
    );

    public invoiceUploadModals = [
        new Modal({
            bodyComponent: BuyerAccountInvoiceUploadModalComponent,
            title: this._i18n('Upload Invoices'),
            id: 'buyer-account-upload',
            showCloseButton: true,
            showFooter: false,
            showOkButton: false
        }),
        new InvoiceUploadSuccessModal()
    ];
    public supplierUploadModals = [
        new Modal({
            bodyComponent: BuyerAccountSupplierUploadModalComponent,
            title: this._i18n('Upload Suppliers'),
            id: 'buyer-account-upload',
            showCloseButton: true,
            showFooter: false,
            showOkButton: false
        }),
        new SupplierUploadSuccessModal()
    ];
    public remittanceInstructionsUploadModals = [
        new Modal({
            bodyComponent: BuyerAccountRemittanceInstructionsUploadModalComponent,
            title: this._i18n('Upload Remittance Instructions'),
            id: 'buyer-account-upload',
            showCloseButton: true,
            showFooter: false,
            showOkButton: false
        }),
        new RemittanceInstructionsUploadSuccessModal()
    ];
    public routingInstructionsUploadModals = [
        new Modal({
            bodyComponent: BuyerAccountRoutingInstructionsUploadModalComponent,
            title: this._i18n('Upload Routing Instructions'),
            id: 'buyer-account-upload',
            showCloseButton: true,
            showFooter: false,
            showOkButton: false
        }),
        new RoutingInstructionsUploadSuccessModal()
    ];
    constructor(
        private _modalWorkflowService: ModalWorkflowService,
        private _backdropService: BackdropService,
        private _claimService: ClaimService,
        private _i18n: I18n,
        public buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService
    ) {}

    public toggleDropdown(): void {
        this.showDropdown = !this.showDropdown;

        if (this.showDropdown) {
            this._backdropService.openBackdrop({
                onClick: () => (this.showDropdown = false),
                closeOnClick: true
            });
        }
    }

    public toggleInvoiceUploadModal(): void {
        this.showDropdown = false;
        this._modalWorkflowService.showWorkflow(this.invoiceUploadModals);
    }

    public toggleSupplierUploadModal(): void {
        this.showDropdown = false;
        this._modalWorkflowService.showWorkflow(this.supplierUploadModals);
    }

    public toggleRemittanceInstructionUploadModal(): void {
        this.showDropdown = false;
        this._modalWorkflowService.showWorkflow(this.remittanceInstructionsUploadModals);
    }

    public toggleRoutingInstructionUploadModal(): void {
        this.showDropdown = false;
        this._modalWorkflowService.showWorkflow(this.routingInstructionsUploadModals);
    }
}
